import { useTranslation } from 'react-i18next';

import { useAuth } from '@saas-kit/lib';
import { Badge, Card, CardContent, CardHeader, CardTitle } from '@saas-kit/ui';

export function ProfilePage(): JSX.Element {
  const { t } = useTranslation('profile');
  const user = useAuth((s) => s.user);
  const impersonation = useAuth((s) => s.impersonation);
  const selectedTenant = useAuth((s) => s.selectedTenant);

  if (!user) return <></>;

  const actorLabel =
    user.is_platform_admin
      ? t('fields.actor_type_backoffice')
      : t('fields.actor_type_tenant');

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">{t('title')}</h1>
        <p className="text-sm text-muted-foreground">{t('subtitle')}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('fields.email')}</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
            <Row label={t('fields.email')} value={user.email} />
            <Row label={t('fields.display_name')} value={user.display_name ?? '—'} />
            <Row label={t('fields.actor_type')} value={actorLabel} />
            <Row label={t('fields.status')} value={user.status} />
            <Row label={t('fields.user_id')} value={user.id} mono />
            <Row label={t('fields.tenant_id')} value={selectedTenant?.tenant_id ?? '—'} mono />
            <Row label={t('fields.tenant_name')} value={selectedTenant?.name ?? '—'} />
            <Row
              label={t('fields.is_impersonation')}
              value={
                impersonation.active
                  ? t('fields.is_impersonation_yes')
                  : t('fields.is_impersonation_no')
              }
            />
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('actions.edit_unavailable')}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">{t('actions.edit_unavailable')}</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('fields.permissions')}</CardTitle>
        </CardHeader>
        <CardContent>
          {user.effective_permissions.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('fields.permissions_empty')}</p>
          ) : (
            <ul className="flex flex-wrap gap-1.5">
              {user.effective_permissions.map((p) => (
                <li key={p}>
                  <Badge variant="outline">{p}</Badge>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Row({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className={mono ? 'font-mono text-xs text-foreground' : 'text-foreground'}>
        {value}
      </dd>
    </div>
  );
}
